<template>
  <div class="itemBox">
    <div
      class="imgBlock"
      :class="
        compositionImageGroups.length > 0
          ? 'imgBlock-hasIngredientComposition'
          : ''
      "
      :style="
        compositionImageGroups.length > 0
          ? { minHeight: ingredientContainerMinHeight + 'px' }
          : ''
      "
    >
      <ingredient-image-composition
        v-if="compositionImageGroups.length > 0"
        :product="item"
        :compositionImageGroups="compositionImageGroups"
        :scale="scale"
        :offset="[ingredientContainerYOffset, -170]"
        :container-height="140"
        :container-width="250"
      ></ingredient-image-composition>
      <img v-else class="itemImg" :src="image" />
    </div>
    <div class="informBlock">
      <div class="headerBlock">
        <div class="itemName text">
          {{ name }}
          <span
            @click="edit"
            class="titleImgEdit"
            v-if="!isV2EditOrder && canEditItem"
          />
        </div>
        <div class="itemAction">
          <count-block
            v-if="productPrice(item) > 0.0"
            :basketitem="item"
            @add="add"
            @sub="sub"
          ></count-block>
          <div class="itemPrice">
            <div
              :class="{
                stroke: coupon || globalCoupon,
                green: productPrice(item) < 0.0
              }"
            >
              {{ item.vatDetails.grossNoDiscounts.toPrintable() }}
            </div>
            <div class="couponPrice" v-if="coupon || globalCoupon">
              {{ item.vatDetails.grossPaid.toPrintable() }}
            </div>
          </div>
        </div>
      </div>
      <div class="collapsedBlock">
        <div class="optionBlock">
          <div class="optionItem">
            <extras-block
              :scanned="item.scanned"
              :extras="extras"
              @editExtra="editExtra"
            ></extras-block>
            <div class="optionLine">
              <ingredients-block
                :ingredients="ingredients"
                :item="item"
                :noprices="noprices"
              ></ingredients-block>
            </div>
            <div v-if="coupon" class="couponBlock optionLine">
              {{ couponDescription(coupon, false) }}
            </div>
            <div v-if="globalCoupon" class="couponBlock optionLine">
              {{ couponDescription(globalCoupon, true) }}
            </div>
            <div v-if="item.deposit" class="depositBlock optionLine">
              {{ $t("order.deposit") }} +{{ toPrintable(this.item.deposit) }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="editRemoveBlock">
      <edit-button v-if="isV2EditOrder && showEditButton" @edit="edit" />
      <remove-button @remove="del" />
    </div>
  </div>
</template>

<script>
import { isEmpty } from "lodash";
import CountBlock from "./common/countBlock";
import {
  toPrintable,
  productPrice,
  ingredientPrice,
  hideZero,
  COUPON_CATEGORIES,
  productPriceWithoutDeposit
} from "@/utils/pricing";
import IngredientsBlock from "./common/ingredientsBlock";
import ExtrasBlock from "./common/extrasBlock";
import IngredientImageComposition from "@/components/common/IngredientImageComposition";
import { mapGetters } from "vuex";
import { getTranslation } from "@/utils/helpers";
import {
  generateCompositionImageGroups,
  getMaxCompositionImageHeight,
  getMaxProductImgHeight,
  getMinTopOffset
} from "@/utils/compositionImageUtil";
import RemoveButton from "@/components/order/listItems/common/removeButton.vue";
import EditButton from "@/components/order/listItems/common/editButton.vue";
import { isV2EditOrder } from "@/utils/templateSettings";

export default {
  name: "ProductItem",
  components: {
    RemoveButton,
    EditButton,
    ExtrasBlock,
    IngredientsBlock,
    CountBlock,
    IngredientImageComposition
  },
  data() {
    return {
      scale: 0.35
    };
  },
  computed: {
    ...mapGetters({
      coupons: "COUPONS",
      product_attributes: "PRODUCT_ATTRIBUTES"
    }),
    // TODO documentation
    ingredientContainerYOffset() {
      return -getMinTopOffset(this.compositionImageGroups, this.item);
    },
    productAttributes() {
      return this.item.attributes;
    },
    // TODO documentation
    ingredientContainerMinHeight() {
      let minTopOffset = getMinTopOffset(
        this.compositionImageGroups,
        this.item
      );
      let maxProductImgHeight = getMaxProductImgHeight(this.item);
      let maxCompositionImageHeight = getMaxCompositionImageHeight(
        this.compositionImageGroups
      );
      return Math.round(
        (Math.max(maxProductImgHeight, maxCompositionImageHeight) -
          minTopOffset) *
          this.scale
      );
    },
    compositionImageGroups() {
      return generateCompositionImageGroups(this.item);
      //return [];
    },
    image() {
      if (this.item.selectedSize && this.item.selectedSize.alternate_picture) {
        return this.item.selectedSize.alternate_picture;
      }
      return this.item.image;
    },
    name() {
      let basename = getTranslation(
        this.$store,
        "Product",
        "display_name",
        this.item.id,
        this.item.name
      );
      if (this.item.selectedSize) {
        let size = this.item.selectedSize;
        let size_name = getTranslation(
          this.$store,
          "Size",
          "display_name",
          size.id,
          size.size
        );
        basename += ` (${size_name})`;
      }
      return basename;
    },
    coupon() {
      if (this.item.coupon) {
        return { ...this.item.coupon, deletable: true };
      }
      return null;
    },
    globalCoupon() {
      if (this.item.count === 1 && this.item.coupon) return null;
      let percent_off_global = this.coupons.find(
        c => c.data.c === COUPON_CATEGORIES.PERCENT_OFF
      );
      if (percent_off_global) {
        return {
          ...percent_off_global,
          data: {
            ...percent_off_global.data,
            c: COUPON_CATEGORIES.PERCENT_OFF_CATEGORY
          },
          deletable: false
        };
      }
      return null;
    },
    showEditButton() {
      return this.canEditItem;
    },
    canEditItem() {
      if (isEmpty(this.item.ingredients)) return false;
      return Object.values(this.item.ingredients).some(o => !isEmpty(o));
    },
    extras() {
      if (this.item.selectedExtras) {
        return this.item.selectedExtras;
      }
      return [];
    },
    ingredients() {
      if (!isEmpty(this.item.ingredients)) {
        return this.item.ingredients;
      }
      return { default: [] };
    },
    isV2EditOrder
  },
  methods: {
    getTranslation,
    toPrintable,
    ingredientPrice,
    hideZero,
    productPrice,
    add() {
      this.$emit("add", this.item);
    },
    sub() {
      this.$emit("sub", this.item);
    },
    edit() {
      this.$emit("edit", this.item);
    },
    editExtra(extra) {
      this.$emit("editExtra", { item: this.item, extra });
    },
    del() {
      this.$emit("del", this.item);
    },
    couponDescription(coupon, is_global) {
      let prefix = "";
      if (this.item.count > 1) {
        if (is_global) {
          prefix = this.item.count - (this.item.coupon ? 1 : 0) + "x ";
        } else {
          prefix = "1x ";
        }
      }
      if (coupon.data.c === COUPON_CATEGORIES.AMOUNT_OFF_CATEGORY) {
        let amount = toPrintable(-1 * coupon.data.a);
        return `${prefix} Coupon (${amount}€ ${this.$t("order.discount")})`;
      }
      if (coupon.data.c === COUPON_CATEGORIES.PERCENT_OFF_CATEGORY) {
        let amount = parseInt(Math.round(coupon.data.a));
        return `${prefix} Coupon (${amount}% ${this.$t("order.discount")})`;
      }
      return "Coupon";
    }
  },
  props: {
    item: Object,
    noprices: Boolean
  }
};
</script>

<style scoped lang="scss">
.invisible {
  opacity: 0;
}
.itemImg {
  height: 130px;
  object-fit: contain;
}
.imgBlock {
  flex: 0 0 25%;
  justify-content: center;
  display: flex;
  pointer-events: none;
  position: relative;
  &-hasIngredientComposition {
    margin-top: var(--component_ProductItem_imgBlock_margin-top);
    min-height: var(--component_ProductItem_imgBlock_min-height);
  }
}
.informBlock {
  width: 72%;
}
.headerBlock {
  display: flex;
  justify-content: space-between;
}
.itemAction {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.itemName {
  font-family: var(--component_ProductItem_itemName_font-family);
  font-size: var(--component_ProductItem_itemName_font-size);
  font-weight: bold;
  letter-spacing: 1.1px;
  color: var(--component_ProductItem_itemName_color);
  text-transform: var(--component_ProductItem_itemName_text-transform);
}

.itemPrice {
  width: var(--component_order_listitems_ProductItem_itemPrice_width);
  justify-content: var(
    --component_order_listitems_ProductItem_itemPrice_justify-content
  );
  font-family: var(--component_ProductItem_itemPrice_font-family);
  color: var(--component_ProductItem_itemPrice_font-color);
  font-size: var(--component_ProductItem_itemPrice_font-size);
  display: flex;
  align-items: var(
    --component_order_listitems_ProductItem_itemPrice_align-items
  );
  padding-right: 10px;
  flex-direction: column;
}

.itemPrice .stroke {
  text-decoration: line-through;
}

.couponPrice {
  color: #62a200;
  text-decoration: none currentcolor solid;
}

.optionItem {
  display: flex;
  flex-direction: column;
}

.optionBlock {
  display: flex;
  width: 100%;
  min-height: 135px;
}

.optionLine {
  display: flex;
  flex-direction: row;
}

.green {
  color: #62a200;
}

.couponBlock {
  margin-top: 10px;
  color: #62a200;
  font-family: var(--app_font-family_regular);
  font-size: 20px;
  font-weight: bold;
  letter-spacing: 1.1px;
}

.depositBlock {
  color: #252525;
  font-family: var(
    --component_order_listitems_ProductItem_depositBlock_font-family
  );
  font-size: var(
    --component_order_listitems_ProductItem_depositBlock_font-size
  );
  font-weight: normal;
  font-style: italic;
}

.titleImgEdit {
  background-image: var(
    --component_order_listitems_ProductItem_titleImgEdit_background-image
  );
  background-size: contain;
  display: inline-block;
  width: 18px;
  height: 18px;
}
</style>
